import type { Api } from "../api/client.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { refs } from "../ui/inert.js";
import { checkedSheet, openSheet, receiptSheet, type SheetHandle } from "../ui/sheet.js";
import { describeStep } from "./describe.js";
import { heldCopy } from "./notices.js";
import type { ReceiptContext, ReceiptModel } from "./receipt-model.js";

/**
 * A receipt as the bank's sheet over a screen — Activity's receipts (§6.13) and, since DCTA-229, the Invest tab's orders
 * and held buys: one implementation, lifted out of `ActivityScreen` unchanged.
 *
 * Verified opens the proof when judge mode is on, else the four-line customer sheet — whose last line is said only when
 * the bank's own check of the trail (`GET /v1/audit/verify`) holds. A held operation can be cancelled from its receipt: a
 * light in-sheet question (06's pattern), then the gateway's one cancel route, then the screen reads again and the
 * receipt reopens as the bank now reads it. While the cancel is in flight the sheet cannot be dragged or tapped away.
 */
export interface ReceiptSheetsDeps {
  readonly api: Pick<Api, "verifyAudit" | "cancelBundle">;
  readonly host: HTMLElement;
  readonly reduced: () => boolean;
  readonly judgeOn: () => boolean;
  readonly onProof: (bundleId: string) => void;
  /** The screen's regions behind a sheet (its topbar, body and dock). */
  readonly behind: () => readonly HTMLElement[];
}

export interface OpenReceipt {
  readonly model: ReceiptModel;
  /** The screen's reads (the held bundle's signed steps are read from it for the question's words). */
  readonly context: () => ReceiptContext | undefined;
  /** The screen reads again after a cancel. */
  readonly refresh: () => Promise<void>;
  /** The same receipt again: "Keep it", and after a cancel (as the bank now reads it). */
  readonly reopen: () => void;
  /** DCTA-209 (30b, 30c): the receipt's schedule row — the receipt closes first. */
  readonly onSchedule?: (mandateId: string) => void;
}

export class ReceiptSheets {
  readonly #d: ReceiptSheetsDeps;
  #sheet: SheetHandle | undefined;
  #busy = false;

  constructor(deps: ReceiptSheetsDeps) {
    this.#d = deps;
  }

  get isOpen(): boolean {
    return this.#sheet !== undefined;
  }

  open(o: OpenReceipt): void {
    if (this.#sheet !== undefined) return;
    const { model: m, onSchedule } = o;
    const { bundleId, cancelHeld } = m;
    const sheet = receiptSheet(m, {
      onDone: () => void this.close(),
      ...(bundleId === undefined ? {} : { onVerified: () => void this.#verified(bundleId) }),
      ...(cancelHeld === undefined ? {} : { onCancelHeld: () => this.#askCancel(sheet, o, cancelHeld.bundleId) }),
      ...(onSchedule === undefined ? {} : { onSchedule: (id: string) => void this.close().then(() => onSchedule(id)) }),
    });
    this.#show(sheet, true);
  }

  /** Drag, tap above, Esc (§C.8): the sheet closes; a Cancel it? question means "keep it". */
  #show(sheet: HTMLElement, animate: boolean): void {
    this.#sheet = openSheet(this.#d.host, sheet, { behind: this.#d.behind(), reduced: this.#d.reduced(), ...(animate ? { animate } : {}), onDismiss: () => void this.close() });
  }

  async close(): Promise<void> {
    const sheet = this.#sheet;
    this.#sheet = undefined;
    await sheet?.close();
  }

  async #verified(bundleId: string): Promise<void> {
    if (this.#d.judgeOn()) {
      await this.close();
      this.#d.onProof(bundleId);
      return;
    }
    // The bank's own check of the trail decides the fourth line — asked now, not assumed.
    const holds = await this.#d.api.verifyAudit(bundleId).then(
      (v) => v.ok,
      () => false,
    );
    await this.close();
    this.#show(
      checkedSheet(holds, () => void this.close()),
      false,
    );
  }

  /** 06 from a receipt: the question in the sheet — Cancel it (the fast path) or Keep it. */
  #askCancel(sheet: HTMLElement, o: OpenReceipt, bundleId: string): void {
    const b = o.context()?.bundles.get(bundleId);
    const acts = sheet.querySelector(".acts");
    if (b === undefined || acts === null) return;
    const heldIds = new Set(b.receipt.steps.filter((r) => r.state === "held").map((r) => r.step_id ?? r.index));
    const held = b.view.signing_payload.steps.filter((s) => heldIds.has(s.step_id) || heldIds.has(s.index));
    // DCTA-143: a flagged payee name is said in its inert form here too, set apart as data.
    const flags = b.view.firewall?.data_flags ?? [];
    const copy = heldCopy(held, flags);
    const inert = held.flatMap((s) => describeStep(s, flags).inert ?? []);
    const yes = el("button", { class: "raw btn", type: "button" }, ["Cancel it"]);
    const no = el("button", { class: "raw btn ghost", type: "button" }, ["Keep it"]);
    acts.replaceChildren(
      el("div", { class: "notice hold" }, [icon("i-clock"), el("div", { class: "ntxt" }, [el("b", {}, refs(copy.cancelAsk.lead, inert)), copy.cancelAsk.rest])]),
      yes,
      no,
    );
    yes.focus({ preventScroll: true });
    no.addEventListener("click", () => {
      void this.close().then(() => o.reopen());
    });
    yes.addEventListener("click", () => {
      if (this.#busy) return;
      this.#busy = true;
      yes.setAttribute("aria-busy", "true");
      // The request is in flight: the sheet cannot be dragged or tapped away until it resolves (§C.8).
      this.#sheet?.lock(true);
      this.#d.api
        .cancelBundle(bundleId, "customer cancelled the held part")
        .then(() => this.close())
        .then(() => o.refresh())
        .then(() => o.reopen())
        .catch(() => {
          yes.setAttribute("aria-busy", "false");
          this.#sheet?.lock(false);
        })
        .finally(() => {
          this.#busy = false;
        });
    });
  }
}
